/**
 * Tectonic plates.
 *
 * Pure noise produces continents that look like spilled paint: round, evenly
 * scattered, with mountains sprinkled at random. Real worlds have a skeleton —
 * landmasses sit on plates, and mountains stand where plates meet. Modelling that
 * crudely costs very little and is the difference between a map that looks generated
 * and one that looks like somewhere.
 *
 * This is not a tectonic simulation. Plates are Voronoi cells around scattered
 * centres; each is designated oceanic or continental, which sets its base height;
 * and tiles near a boundary get uplift scaled by what kind of boundary it is. That
 * gives coherent continents, mountain chains along collisions, and island arcs where
 * two ocean plates meet.
 */

import { chance, nextFloat, nextInt, type RngState } from '../rng/index.js';
import { createNoise, cylindricalFbm } from './noise.js';
import type { MapgenParams } from './params.js';

export interface Plate {
  col: number;
  row: number;
  oceanic: boolean;
  /** Base height of the plate's interior, before noise and uplift, in [0, 1]. */
  baseElevation: number;
  /** Direction of travel. Where two plates meet decides whether they build or tear. */
  driftX: number;
  driftY: number;
}

export interface PlateField {
  plates: Plate[];
  /** Index of the plate owning each tile. */
  owner: Int16Array;
  /** 0 in a plate's interior, rising to 1 at a boundary. */
  boundary: Float32Array;
  /** Uplift contribution at each tile, already scaled by boundary type, in [0, 1]. */
  uplift: Float32Array;
  /**
   * Plate base height per tile, blended smoothly across boundaries.
   *
   * Using each plate's height as a flat value makes its Voronoi cell visible in the
   * finished map as a polygon with straight edges — and leaves the cell's interior so
   * uniform that every river in it runs the same direction. Blending toward the
   * neighbouring plate as a tile approaches the seam removes both.
   */
  base: Float32Array;
}

/** Column distance that respects east-west wrap. */
function columnDelta(a: number, b: number, width: number, wrapX: boolean): number {
  const raw = Math.abs(a - b);
  return wrapX ? Math.min(raw, width - raw) : raw;
}

/**
 * How many plates a map of this size gets.
 *
 * Expressed as a target area per plate rather than a fixed count, because the count
 * has to scale with the map. Held constant, a big map gets a coarser tectonic grain
 * than a small one — which is exactly backwards, and showed up as large and huge maps
 * having far bigger empty oceans than standard ones from the same parameters.
 */
export function plateCountFor(width: number, height: number, params: MapgenParams): number {
  // The floor matters at the small end: on a duel map, pure area scaling gives six
  // plates, which is too few for the world to have any tectonic structure at all —
  // one oceanic plate then covers a third of the map.
  return Math.max(params.minPlates, Math.round((width * height) / params.tilesPerPlate));
}

export function generatePlates(
  width: number,
  height: number,
  wrapX: boolean,
  params: MapgenParams,
  rng: RngState,
): PlateField {
  const plates: Plate[] = [];
  const plateCount = plateCountFor(width, height, params);

  // Scatter centres with a crude minimum-distance rejection, so plates are roughly
  // even in size. Fully random centres produce one huge plate and several slivers.
  const minSpacing = (Math.min(width, height) / Math.sqrt(plateCount)) * 0.55;
  let attempts = 0;
  while (plates.length < plateCount && attempts < plateCount * 200) {
    attempts++;
    const col = nextInt(rng, 0, width);
    const row = nextInt(rng, 0, height);
    const tooClose = plates.some((p) => {
      const dx = columnDelta(col, p.col, width, wrapX);
      const dy = row - p.row;
      return Math.sqrt(dx * dx + dy * dy) < minSpacing;
    });
    if (tooClose) continue;

    const oceanic = chance(rng, params.oceanicPlateFraction);
    plates.push({
      col,
      row,
      oceanic,
      baseElevation: baseElevationFor(oceanic, rng),
      driftX: nextFloat(rng) * 2 - 1,
      driftY: nextFloat(rng) * 2 - 1,
    });
  }

  // Rejection sampling can fall short on a small map; top up without the spacing rule
  // rather than looping forever or silently generating fewer plates than asked for.
  while (plates.length < plateCount) {
    const oceanic = chance(rng, params.oceanicPlateFraction);
    plates.push({
      col: nextInt(rng, 0, width),
      row: nextInt(rng, 0, height),
      oceanic,
      baseElevation: baseElevationFor(oceanic, rng),
      driftX: nextFloat(rng) * 2 - 1,
      driftY: nextFloat(rng) * 2 - 1,
    });
  }

  spreadPlateTypes(plates, width, wrapX, params);

  const tiles = width * height;
  const owner = new Int16Array(tiles);
  const boundary = new Float32Array(tiles);
  const uplift = new Float32Array(tiles);
  const base = new Float32Array(tiles);

  const boundaryScale = Math.min(width, height) * params.boundaryWidth;

  // Voronoi cell boundaries are straight lines, and a rift or a mountain chain laid
  // along one reads unmistakably as a ruler-drawn coast. Displacing the position used
  // for the plate lookup — not the tile itself — bends those boundaries into
  // something geological without changing anything else about the model.
  const warpNoise = createNoise(rng);
  const warpAmount = Math.min(width, height) * params.plateWarp;

  for (let row = 0; row < height; row++) {
    for (let col = 0; col < width; col++) {
      const index = row * width + col;

      const sampleCol =
        col + cylindricalFbm(warpNoise, col, row, width, 2.5, { octaves: 3 }) * warpAmount;
      const sampleRow =
        row + cylindricalFbm(warpNoise, col + width * 0.41, row + 57.3, width, 2.5, { octaves: 3 }) * warpAmount;

      // Nearest and second-nearest plate. The gap between them is the standard
      // Voronoi boundary measure: near zero means the tile sits on a seam.
      let nearest = 0;
      let nearestDistance = Infinity;
      let secondDistance = Infinity;
      let secondIndex = 0;

      for (let p = 0; p < plates.length; p++) {
        const plate = plates[p]!;
        const dx = columnDelta(sampleCol, plate.col, width, wrapX);
        const dy = sampleRow - plate.row;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance < nearestDistance) {
          secondDistance = nearestDistance;
          secondIndex = nearest;
          nearestDistance = distance;
          nearest = p;
        } else if (distance < secondDistance) {
          secondDistance = distance;
          secondIndex = p;
        }
      }

      owner[index] = nearest;
      const seam = Math.max(0, 1 - (secondDistance - nearestDistance) / boundaryScale);
      boundary[index] = seam;

      const a = plates[nearest]!;
      const b = plates[secondIndex]!;

      // Are these two plates moving into each other, or away from each other?
      // Project their relative drift onto the line joining their centres: positive
      // means convergence, negative means a rift opening. Without this every
      // boundary builds mountains, plates only ever weld together, and the generator
      // produces one supercontinent on nearly every seed. Rifts are what make oceans.
      let toX = b.col - a.col;
      if (wrapX) {
        if (toX > width / 2) toX -= width;
        if (toX < -width / 2) toX += width;
      }
      const toY = b.row - a.row;
      const length = Math.max(1e-6, Math.sqrt(toX * toX + toY * toY));
      const convergence = ((a.driftX - b.driftX) * toX + (a.driftY - b.driftY) * toY) / length;

      // How much relief the boundary generates, by the kind of crust involved.
      let collisionFactor: number;
      if (!a.oceanic && !b.oceanic) collisionFactor = 1; // continental collision — the great ranges
      else if (a.oceanic !== b.oceanic) collisionFactor = 0.62; // subduction — coastal cordillera
      else collisionFactor = 0.3; // two ocean plates — island arcs

      // Squared falloff keeps ranges narrow and sharp instead of broad and soft.
      // Divergent boundaries take the same shape with the sign flipped: a trench.
      const magnitude = seam * seam * collisionFactor * params.boundaryUplift;
      uplift[index] = convergence >= 0 ? magnitude : -magnitude * params.riftDepth;

      // Blend the two plates' heights near the seam. `blend` is 1 deep inside the
      // owning plate and 0.5 exactly on the boundary, so height crosses smoothly
      // instead of stepping — which is what keeps continents from looking polygonal.
      const blend = 0.5 + 0.5 * Math.min(1, (secondDistance - nearestDistance) / (boundaryScale * 2.5));
      base[index] = b.baseElevation + (a.baseElevation - b.baseElevation) * blend;
    }
  }

  return { plates, owner, boundary, uplift, base };
}

/**
 * Break up runs of same-type plates.
 *
 * Deciding each plate's type independently means several oceanic plates can land
 * side by side and merge into one enormous dead ocean, with all the land crowded
 * into what is left. The map's overall land-to-water ratio looks perfectly healthy
 * while the world is no fun to play on: measured as "how far from land can you get",
 * a clumped map reaches 27 tiles where a well-spread one reaches 13.
 *
 * The fix is a short local search. Build a neighbour graph over the plates, count the
 * edges joining two plates of the same type, and swap pairs to reduce that count.
 * The swap budget is deliberately small — the goal is to break up the worst clumps,
 * not to reach a perfect checkerboard, which would make every map feel alike.
 */
function spreadPlateTypes(plates: Plate[], width: number, wrapX: boolean, params: MapgenParams): void {
  const count = plates.length;
  if (count < 4 || params.plateSpread <= 0) return;

  // Neighbour graph: each plate's nearest few, made symmetric.
  const NEIGHBOURS_PER_PLATE = 4;
  const neighbours: number[][] = plates.map(() => []);
  for (let a = 0; a < count; a++) {
    const distances = plates
      .map((plate, b) => {
        const dx = columnDelta(plates[a]!.col, plate.col, width, wrapX);
        const dy = plates[a]!.row - plate.row;
        return { b, distance: Math.sqrt(dx * dx + dy * dy) };
      })
      .filter((entry) => entry.b !== a)
      .sort((x, y) => x.distance - y.distance)
      .slice(0, NEIGHBOURS_PER_PLATE);
    for (const { b } of distances) {
      if (!neighbours[a]!.includes(b)) neighbours[a]!.push(b);
      if (!neighbours[b]!.includes(a)) neighbours[b]!.push(a);
    }
  }

  const sameTypeNeighbours = (index: number): number =>
    neighbours[index]!.reduce((total, n) => total + (plates[n]!.oceanic === plates[index]!.oceanic ? 1 : 0), 0);

  const clumping = (): number => {
    let total = 0;
    for (let i = 0; i < count; i++) total += sameTypeNeighbours(i);
    return total; // each edge counted twice, which is fine for comparison
  };

  // Swap the worst-placed oceanic plate with the worst-placed continental one, as
  // long as it helps. Swapping preserves the requested oceanic fraction exactly.
  const budget = Math.max(1, Math.round(count * params.plateSpread));
  for (let step = 0; step < budget; step++) {
    let worstOceanic = -1;
    let worstContinental = -1;
    let worstOceanicScore = -1;
    let worstContinentalScore = -1;

    for (let i = 0; i < count; i++) {
      const score = sameTypeNeighbours(i);
      if (plates[i]!.oceanic) {
        if (score > worstOceanicScore) {
          worstOceanicScore = score;
          worstOceanic = i;
        }
      } else if (score > worstContinentalScore) {
        worstContinentalScore = score;
        worstContinental = i;
      }
    }
    if (worstOceanic === -1 || worstContinental === -1) return; // all one type

    const before = clumping();
    swapPlateType(plates[worstOceanic]!, plates[worstContinental]!);
    if (clumping() >= before) {
      // No improvement: put them back and stop, rather than oscillating.
      swapPlateType(plates[worstOceanic]!, plates[worstContinental]!);
      return;
    }
  }
}

/**
 * Exchange two plates' crust types and their heights together.
 *
 * An exchange rather than a re-roll: re-rolling would draw from the random stream on
 * every swap, so the number of swaps a map happened to need would shift every later
 * random decision in the generator. Exchanging keeps the set of plate heights
 * identical and only moves them around.
 */
function swapPlateType(a: Plate, b: Plate): void {
  const oceanic = a.oceanic;
  const elevation = a.baseElevation;
  a.oceanic = b.oceanic;
  a.baseElevation = b.baseElevation;
  b.oceanic = oceanic;
  b.baseElevation = elevation;
}

/**
 * Base height of a plate's interior. Oceanic and continental ranges never overlap,
 * so a continent survives the sea-level cut as a unit rather than in fragments.
 */
function baseElevationFor(oceanic: boolean, rng: RngState): number {
  return oceanic ? 0.16 + nextFloat(rng) * 0.16 : 0.58 + nextFloat(rng) * 0.26;
}
